import { Capacitor } from '@capacitor/core';
import { auth } from '../config/firebase';
import { URL_PUBLICA } from '../config/marca';
import { REVENUECAT, TipoCompra } from '../config/planes';

/**
 * Compras de myarinn Plus, igual en todas las plataformas, a través de
 * RevenueCat:
 *   · iOS     → compra integrada de Apple (obligatoria para algo digital, norma 3.1.1)
 *   · Android → Google Play Billing (cuando haya app de Android)
 *   · Web     → RevenueCat Web Billing (cobra con Stripe)
 *
 * El usuario de RevenueCat es el uid de Firebase, así que lo que se paga en un
 * sitio vale en todos. La app nunca se da Plus a sí misma: tras pagar le pide
 * al servidor (plus-sincronizar) que lo compruebe con RevenueCat y lo apunte
 * en `usuarios/{uid}`; el webhook de RevenueCat hace lo mismo con las
 * renovaciones y cancelaciones.
 *
 * Sin la clave de la plataforma (REACT_APP_REVENUECAT_*) no se ofrece comprar.
 */

const plataforma = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

const CLAVE: string | undefined = {
  ios: process.env.REACT_APP_REVENUECAT_IOS_KEY,
  android: process.env.REACT_APP_REVENUECAT_ANDROID_KEY,
  web: process.env.REACT_APP_REVENUECAT_WEB_KEY,
}[plataforma];

export const comprasDisponibles = Boolean(CLAVE);

/** Dónde se gestiona (o cancela) la suscripción, según dónde se pagó. */
export const GESTIONAR_SUSCRIPCION: Record<string, string> = {
  app_store: 'https://apps.apple.com/account/subscriptions',
  play_store: 'https://play.google.com/store/account/subscriptions',
};

export interface OpcionCompra {
  tipo: TipoCompra;
  /** Precio formateado por la tienda, con su moneda. */
  precio: string;
  /** Días de prueba gratis que ofrece la tienda (p. ej. 14 en la anual); 0 si no hay. */
  diasPrueba: number;
  paquete: unknown;
}

let configuradoPara: string | null = null;

async function nativo() {
  const { Purchases } = await import('@revenuecat/purchases-capacitor');
  return Purchases;
}

async function web() {
  const { Purchases } = await import('@revenuecat/purchases-js');
  return Purchases;
}

async function configurar(uid: string) {
  if (!CLAVE) throw new Error('Las compras no están disponibles todavía.');
  if (configuradoPara === uid) return;
  if (plataforma === 'web') {
    const P = await web();
    if (P.isConfigured()) await P.getSharedInstance().changeUser(uid);
    else P.configure({ apiKey: CLAVE, appUserId: uid });
  } else {
    const P = await nativo();
    if (configuradoPara) await P.logIn({ appUserID: uid });
    else await P.configure({ apiKey: CLAVE, appUserID: uid });
  }
  configuradoPara = uid;
}

const DIAS: Record<string, number> = { day: 1, week: 7, month: 30, year: 365 };
const diasDe = (unidad: string | undefined, n: number | undefined) =>
  (DIAS[(unidad ?? '').toLowerCase()] ?? 0) * (n ?? 0);

const TIPO_DE_PAQUETE = Object.fromEntries(
  Object.entries(REVENUECAT.paquetes).map(([tipo, id]) => [id, tipo as TipoCompra]),
);

/** Las opciones a la venta, con el precio de cada tienda. */
export async function opcionesDeCompra(uid: string): Promise<OpcionCompra[]> {
  await configurar(uid);
  const paquetes: { identifier: string; precio: string; diasPrueba: number; paquete: unknown }[] = [];
  if (plataforma === 'web') {
    const ofertas = await (await web()).getSharedInstance().getOfferings({ currency: 'EUR' });
    ofertas.current?.availablePackages.forEach(p =>
      paquetes.push({
        identifier: p.identifier, precio: p.product.currentPrice.formattedPrice, paquete: p,
        diasPrueba: diasDe(p.product.freeTrialPhase?.period?.unit, p.product.freeTrialPhase?.period?.number),
      }));
  } else {
    const ofertas = await (await nativo()).getOfferings();
    ofertas.current?.availablePackages.forEach(p =>
      paquetes.push({
        identifier: p.identifier, precio: p.product.priceString, paquete: p,
        // Apple da la prueba como precio de lanzamiento a 0
        diasPrueba: p.product.introPrice?.price === 0
          ? diasDe(p.product.introPrice.periodUnit, p.product.introPrice.periodNumberOfUnits) : 0,
      }));
  }
  return paquetes
    .filter(p => TIPO_DE_PAQUETE[p.identifier])
    .map(p => ({ tipo: TIPO_DE_PAQUETE[p.identifier], precio: p.precio, diasPrueba: p.diasPrueba, paquete: p.paquete }));
}

/** true si se ha pagado; false si el usuario ha cancelado. Lanza si falla. */
export async function comprar(uid: string, opcion: OpcionCompra): Promise<boolean> {
  await configurar(uid);
  try {
    if (plataforma === 'web') {
      const P = (await web()).getSharedInstance();
      await P.purchase({ rcPackage: opcion.paquete as any, customerEmail: auth.currentUser?.email ?? undefined });
    } else {
      await (await nativo()).purchasePackage({ aPackage: opcion.paquete as any });
    }
  } catch (e: any) {
    // Cancelar no es un error: web → errorCode 1, nativo → userCancelled
    if (e?.userCancelled || e?.errorCode === 1) return false;
    throw e;
  }
  await sincronizar();
  return true;
}

/** «Restaurar compras»: lo exige Apple para quien reinstala o cambia de iPhone. */
export async function restaurar(uid: string): Promise<void> {
  await configurar(uid);
  if (plataforma !== 'web') await (await nativo()).restorePurchases();
  await sincronizar();
}

/** Pide al servidor que lea el estado en RevenueCat y lo apunte en el perfil. */
export async function sincronizar(): Promise<void> {
  const usuario = auth.currentUser;
  if (!usuario) return;
  // La web llama a su propio dominio (así una vista previa de Netlify usa su
  // propia función y no choca con CORS); la app, a myarinn.app.
  const base = plataforma === 'web' ? '' : URL_PUBLICA;
  const r = await fetch(`${base}/.netlify/functions/plus-sincronizar`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${await usuario.getIdToken()}` },
  });
  if (!r.ok) {
    const datos = await r.json().catch(() => ({}));
    throw new Error(datos.error || `El servidor ha contestado ${r.status}.`);
  }
}
